/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";

import Preview from "./Preview";
import Service from "../../../../api/service";
import { getType } from "../contentStats/helper";
import { Modal } from "../../component/ModalUtils";
import { TextArea } from "../../component/FormUtils";
import { MyButton } from "../../component/ButtonUtils";
import { Pagination } from "../../component/Pagination";
import { _TYPES_ } from "../../../doseeData/utils/types";
import { _PAGE_SIZE_ } from "../../../../middlewares/other/tools";
import { MyActionButton } from "../../component/ActionButtonUtils";
import { useAdminContext } from "../../../../context/admin/context/AdminContext";
import { useDoSeeDataContext } from "../../../../context/doSeeData/DoSeeDataContext";
import {
  pushCountry,
  pushOneEvent,
} from "../../../../middlewares/sysNavigation/SysNavigation";
import { MyMessage } from "../../component/messageUtils";
import { FaRegCheckSquare, FaRegSquare, FaRegTrashAlt } from "react-icons/fa";
import { RiRefreshLine, RiSearchLine } from "react-icons/ri";

const editRoute = "/data/event/edit";

const Overview = () => {
  const [events, setEvents] = useState(null);
  const [count, setCount] = useState(null);
  const [prompt, setPrompt] = useState(null);
  const [promptId, setPromptId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [eventsLoading, setEventsLoading] = useState(false); // UI-only
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(_PAGE_SIZE_);

  const [error, setError] = useState(false);
  const [message, setErrorMessage] = useState("");
  const [success, setSuccess] = useState(false);

  const [selectedContentIds, setSelectedContentIds] = useState([]);
  const [query, setQuery] = useState(""); // UI-only search

  const { deleteOneContent } = useDoSeeDataContext();

  useEffect(() => {
    if (!events) getEvents(page, pageSize);
    if (!prompt) getPrompt(1, 50);
  }, [events, prompt]);

  const getEvents = async (p = 1, size = 50) => {
    setEventsLoading(true);
    const url = `/api/v1/admin/generated/event/?page=${p}&pageSize=${size}`;
    const { data } = await Service.get(url);
    if (data) {
      setCount(data.count);
      setEvents(data.data);
    }
    setEventsLoading(false);
  };

  const getPrompt = async (p = 1, size = 50) => {
    const url = `/api/v1/open_ai/?page=${p}&pageSize=${size}`;
    const { data } = await Service.get(url);

    if (data && data?.data) {
      setPrompt(data?.data[0]?.prompt);
      setPromptId(data?.data[0]?.open_ai_helper_id);
    }
  };

  const savePrompt = async (e) => {
    e.preventDefault();
    setLoading(true);

    const url = `/api/v1/open_ai/update/${promptId}`;
    const { status } = await Service.put(url, {
      cron_day_one: 1,
      cron_day_two: 4,
      prompt: prompt,
    });

    if (status === 200 || status === 201) {
      responses(false, message);
    } else {
      responses(true, message);
    }

    setLoading(false);
  };

  const responses = (isError, msg) => {
    setError(isError);
    setLoading(false);
    setSuccess(!isError);
    setErrorMessage(msg);
  };

  const deleteMany = async () => {
    if (!selectedContentIds?.length) return;
    setLoadingDelete(true);

    for (let i = 0; i < selectedContentIds?.length; i++) {
      await deleteOneContent({
        data_id: selectedContentIds[i],
        content: _TYPES_.EVENT,
      });

      const filtered = (events || []).filter(
        (it) => it.event_id !== selectedContentIds[i]
      );
      setEvents(filtered);
    }

    setLoadingDelete(false);
    setSelectedContentIds([]);
    getEvents(page, pageSize);
  };

  // ---------- UI-only derived data ----------
  const filteredEvents = useMemo(() => {
    if (!events) return null;
    if (!query?.trim()) return events;
    const q = query.toLowerCase();
    return events.filter((e) => (e?.title || "").toLowerCase().includes(q));
  }, [events, query]);

  const allVisibleSelected =
    filteredEvents?.length &&
    filteredEvents.every((e) => selectedContentIds.includes(e?.event_id));

  const toggleSelectAllVisible = () => {
    if (!filteredEvents?.length) return;
    if (allVisibleSelected) {
      const visibleIds = new Set(filteredEvents.map((e) => e?.event_id));
      setSelectedContentIds((ids) => ids.filter((id) => !visibleIds.has(id)));
    } else {
      const add = filteredEvents
        .map((e) => e?.event_id)
        .filter((id) => !selectedContentIds.includes(id));
      setSelectedContentIds([...selectedContentIds, ...add]);
    }
  };

  const clearSelection = () => setSelectedContentIds([]);

  return (
    <div className="p-3 lg:p-5 h-full overflow-y-auto rounded-b-xl bg-gradient-to-b from-white to-gray-50">
      {/* Global message */}
      <MyMessage
        error={error}
        message={message}
        success={success}
        onExpired={() => {
          setError(false);
          setSuccess(false);
        }}
        onClick={() => {
          setError(false);
          setSuccess(false);
          setErrorMessage("");
        }}
      />

      {/* Prompt card */}
      <form
        onSubmit={savePrompt}
        className="w-full max-w-5xl mx-auto mb-6 rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden"
      >
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-gray-100">
          <div>
            <h2 className="text-sm font-semibold text-gray-800">
              Content Generation Prompt
            </h2>
            <p className="text-xs text-gray-500">
              Tweak the description used to generate events.
            </p>
          </div>
          <MyActionButton
            title="Save Changes"
            loading={loading}
            className="bg-black hover:bg-black/80 py-2 px-4"
          />
        </div>
        <div className="p-4 sm:p-6">
          <TextArea
            props={{
              id: "prompt",
              title: "Prompt Description",
              type: "text",
              value: prompt ? prompt : "",
              onChange: (e) => setPrompt(e.target.value),
            }}
          />
          {/* Local helper: character count (UI only) */}
          <div className="mt-2 text-xs text-gray-500 flex items-center justify-end">
            {(prompt?.length || 0).toLocaleString()} characters
          </div>
        </div>
      </form>

      {/* Events section */}
      <div className="w-full max-w-6xl mx-auto">
        {/* Toolbar */}
        <div className="mb-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2">
            <MyButton className="text-white flex items-center space-x-2">
              {allVisibleSelected ? (
                <FaRegCheckSquare
                  onClick={toggleSelectAllVisible}
                  className="text-green-600 cursor-pointer text-lg"
                />
              ) : (
                <FaRegSquare
                  onClick={toggleSelectAllVisible}
                  className="cursor-pointer text-white text-lg"
                />
              )}
              <p className="text-white">
                {allVisibleSelected ? "Unselect Visible" : "Select Visible"}
              </p>
            </MyButton>

            <MyButton onClick={deleteMany} className="text-white flex items-center space-x-2">
              {loadingDelete ? (
                <RiRefreshLine className="text-white text-lg animate-spin" />
              ) : (
                <FaRegTrashAlt className="text-red-600 cursor-pointer text-base" />
              )}
              <p className="text-white">Delete {selectedContentIds?.length || 0} Item(s)</p>
            </MyButton>

            {selectedContentIds?.length ? (
              <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700">
                {selectedContentIds.length} selected
              </span>
            ) : null}
          </div>

          <div className="flex items-center gap-2">
            {/* Search (UI-only, local filter) */}
            <div className="relative">
              <RiSearchLine className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by title..."
                className="pl-9 pr-3 py-2 text-sm rounded-xl border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
              />
            </div>

            <MyButton
              onClick={() => getEvents(page, pageSize)}
              className="flex items-center gap-2 bg-white border border-gray-300 hover:bg-gray-50 rounded-xl px-3 py-2"
            >
              <RiRefreshLine className="text-white" />
              <span className="text-sm text-white">Refresh</span>
            </MyButton>

            {selectedContentIds?.length ? (
              <MyButton
                onClick={clearSelection}
                className="text-sm text-white bg-white border border-gray-300 hover:bg-gray-50 rounded-xl px-3 py-2"
              >
                Clear
              </MyButton>
            ) : null}
          </div>
        </div>

        {/* Table card */}
        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="mb-1 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr className="text-left text-gray-600">
                  <th className="px-3 py-3 font-semibold w-24">Select</th>
                  <th className="px-3 py-3 font-semibold w-40">Image</th>
                  <th className="px-3 py-3 font-semibold">Title</th>
                  <th className="px-3 py-3 font-semibold text-center w-40">Action</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100">
                {/* Loading skeleton */}
                {eventsLoading && (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={`sk-${i}`} className="animate-pulse">
                      <td className="px-3 py-3">
                        <div className="h-5 w-5 rounded bg-gray-200" />
                      </td>
                      <td className="px-3 py-3">
                        <div className="h-16 w-16 rounded-md bg-gray-200" />
                      </td>
                      <td className="px-3 py-3">
                        <div className="h-4 w-56 rounded bg-gray-200 mb-2" />
                        <div className="h-3 w-40 rounded bg-gray-100" />
                      </td>
                      <td className="px-3 py-3">
                        <div className="h-8 w-24 rounded bg-gray-200 ml-auto" />
                      </td>
                    </tr>
                  ))
                )}

                {/* Empty state */}
                {!eventsLoading && filteredEvents && filteredEvents.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-6 py-12 text-center">
                      <div className="mx-auto max-w-sm">
                        <div className="mx-auto h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center">
                          <RiSearchLine className="text-gray-400 text-xl" />
                        </div>
                        <h3 className="mt-3 text-sm font-semibold text-gray-800">No results</h3>
                        <p className="mt-1 text-xs text-gray-500">
                          Try adjusting your search or refresh the list.
                        </p>
                      </div>
                    </td>
                  </tr>
                )}

                {/* Data rows */}
                {!eventsLoading && filteredEvents
                  ? filteredEvents.map((evt, key) => {
                    const isSelected = selectedContentIds.includes(evt?.event_id);
                    return (
                      <tr
                        key={key}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-3 py-3">
                          {isSelected ? (
                            <MyButton
                              onClick={() => {
                                const filteredIds = selectedContentIds.filter(
                                  (id) => id !== evt?.event_id
                                );
                                setSelectedContentIds(filteredIds);
                              }}
                            >
                              <FaRegCheckSquare className="text-green-600 cursor-pointer text-lg" />
                            </MyButton>
                          ) : (
                            <MyButton
                              onClick={() =>
                                setSelectedContentIds([
                                  ...selectedContentIds,
                                  evt?.event_id,
                                ])
                              }
                            >
                              <FaRegSquare className="cursor-pointer text-gray-800 text-lg" />
                            </MyButton>
                          )}
                        </td>

                        <td className="px-3 py-3">
                          <div className="flex items-center">
                            <img
                              src={evt?.thumbnail}
                              alt={evt?.title}
                              className="w-16 h-16 object-cover rounded-lg ring-1 ring-gray-200"
                            />
                          </div>
                        </td>

                        <td className="px-3 py-3">
                          <div className="max-w-[38ch]">
                            <div className="font-semibold text-gray-900 truncate">
                              {evt?.title}
                            </div>
                            <div className="mt-1 text-xs text-gray-500">
                              {new Date(evt?.createdAt).toDateString()} •{" "}
                              {new Date(evt?.createdAt).toLocaleTimeString()}
                            </div>
                          </div>
                        </td>

                        <td className="px-3 py-3">
                          <div className="flex justify-center">
                            <ActionButton
                              props={evt}
                              reloadData={() => getEvents(page, pageSize)}
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })
                  : null}

                {/* Fallback initial row (kept from original) */}
                {!events && !eventsLoading && (
                  <tr>
                    <td className="px-3 py-3">...</td>
                    <td className="px-3 py-3">...</td>
                    <td className="px-3 py-3">...</td>
                    <td className="px-3 py-3">...</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        <div className="mt-4">
          <Pagination
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            total_items={count}
            callbackFunction={(p, size) => {
              getEvents(p, size);
            }}
          />
        </div>
      </div>
    </div>
  );
};

/**
 *  @author Ntavigwa Bashombe
 * @param {*} props
 * @returns
 */
const ActionButton = ({ props, reloadData }) => {
  const { sysAdmin, setSingleAdmin } = useAdminContext();
  const isSuperAdmin = ["super_admin", "admin"].includes(sysAdmin?.role);
  const { deleteOneContent, updateOneContent } = useDoSeeDataContext();

  const [showModal, setShowModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const event_id = props.event_id;
  const event_title = props.title;
  const event = props;

  const deleteOneEventById = async (event_id) => {
    const { status } = await deleteOneContent({
      data_id: event_id,
      content: _TYPES_.EVENT,
    });

    setShowModal(false);

    if (status === 200) {
      reloadData();
    }
  };

  const suspension = async (event) => {
    const data = {
      country_id: event?.country_id,
      description: event?.description,
      first_level_id: event?.first_level_id,
      fourth_level_id: event?.fourth_level_id,
      second_level_id: event?.second_level_id,
      slug: event?.title?.toLowerCase().replace(/\s/g, "_"),
      status: event?.status === 1 ? 0 : 1,
      third_level_id: event?.third_level_id,
      thumbnail: event?.thumbnail,
      title: event?.title,
      url_images: event?.url_images,
    };

    setSingleAdmin({
      ids: [sysAdmin?.admin_id],
      type: getType({ field: _TYPES_.EVENT }),
      route: "/generated",
    });

    const { status } = await updateOneContent({
      data_id: event?.event_id,
      data: data,
      content: _TYPES_.EVENT,
    });

    if (status === 200) {
      reloadData();
    }
  };

  const onEdit = (event) => {
    pushOneEvent(event);

    pushCountry({
      country: {
        id: 0,
        name: "",
        slug: "",
        route: "/generated",
      },
    });

    setSingleAdmin({
      ids: [sysAdmin?.admin_id],
      type: getType({ field: _TYPES_.EVENT }),
      route: "/generated",
    });
  };

  return (
    <>
      {showPreview ? (
        <Preview
          event={event}
          isSuperAdmin={isSuperAdmin}
          suspension={suspension}
          editRoute={editRoute}
          onEdit={onEdit}
          setShowDeleteModal={setShowModal}
          setShowPreview={setShowPreview}
        />
      ) : null}

      <Modal
        props={{
          name: event_title,
          showModal: showModal,
          setShowModal: () => setShowModal(false),
          delete: () => deleteOneEventById(event_id),
        }}
      />

      <div className="flex flex-row justify-end items-center px-2 py-2 gap-2">
        <MyButton
          title="Preview"
          className="py-2 px-4 bg-black hover:bg-black/80 rounded-xl"
          onClick={() => setShowPreview(true)}
        />
        {/* Kept original edit/delete (commented) to preserve logic parity
        <Link to={editRoute}>
          <MyButton
            title="Edit"
            className="py-2 px-4 bg-blue-800 hover:bg-blue-600 rounded-xl"
            onClick={() => onEdit(event)}
          />
        </Link>
        <MyButton
          title="Delete"
          className="py-2 px-4 bg-red-800 hover:bg-red-600 rounded-xl"
          onClick={() => setShowModal(true)}
        />
        */}
      </div>
    </>
  );
};

export default Overview;
